import React, { useState } from 'react';
import { PRODUCTS, SOLUTIONS, anchorFor } from './catalog';
import { goToSection } from './MarketingNav';
import { AiVisual } from './FeaturesPage';
import { AI_MONTHLY_QUESTIONS, PRO_PARTNER_PRICE, PRO_PRICE, PRO_TRIAL_DAYS, proPriceFor } from '../utils/proPricing';
import {
  IconShield,
  IconCheck,
  IconChart,
  IconZap,
  IconStar,
  IconList,
  IconLock,
  IconPlus,
  IconDashboard,
} from '../components/Icons';

const HOMEPAGE_FAQ = [
  {
    q: 'What is RiskLo?',
    a:
      'RiskLo is a strategy risk assessment dashboard for prop-firm and futures traders. It analyzes historical strategy data and your account configuration to tell you, before you trade, whether a setup is likely to blow your account or violate consistency rules.',
  },
  {
    q: 'Do I need a strategy brand membership to use RiskLo?',
    a:
      'No. My Portfolio, the risk tools and RiskLo AI work for any trader on Pro. Company strategy results come from the company you belong to, so those need a membership with that company.',
  },
  {
    q: 'How is risk calculated?',
    a:
      'RiskLo combines historical strategy P&L with your live inputs (account size, max drawdown, contracts, % rule, start-of-day profit) to compute end-of-day blowout probability, contract-scaled worst losses, and consistency-rule exposure. The full methodology is on the "How we calculate" page.',
  },
  {
    q: 'What can RiskLo AI do?',
    a:
      `It answers questions about your own connected accounts and trades, the market calendar, earnings and news, prop firm rules, and how every RiskLo number is calculated. It explains facts and never gives trading advice. Pro includes ${AI_MONTHLY_QUESTIONS.pro} questions a month.`,
  },
  {
    q: 'Is my data secure?',
    a:
      'Yes. Sign-in is through Google, Microsoft or email, every broker connection is read-only, and RiskLo never places trades. Your account data stays scoped to your account.',
  },
];

function FaqItem({ q, a, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`marketing-faq-item ${open ? 'is-open' : ''}`}>
      <button type="button" className="marketing-faq-q" onClick={() => setOpen((v) => !v)}>
        <span>{q}</span>
        <IconPlus size={18} />
      </button>
      <div className="marketing-faq-a">
        <p>{a}</p>
      </div>
    </div>
  );
}

function LandingPage({ onNavigate, user }) {
  const go = (page) => () => onNavigate && onNavigate(page);
  const isSignedIn = Boolean(user?.email);
  const primaryCtaLabel = isSignedIn ? 'Open dashboard' : 'Start free — sign in';
  const primaryCtaTarget = isSignedIn ? 'results' : 'login';

  return (
    <>
      {/* ============== Hero ============== */}
      <section className="marketing-section marketing-hero">
        <div className="marketing-hero-inner">
          <div className="marketing-eyebrow marketing-reveal">
            <IconShield size={14} /> Strategy risk assessment for traders
          </div>
          <h1 className="marketing-title marketing-reveal" data-delay="1">
            See your <span className="accent">true risk</span><br />before you take the trade.
          </h1>
          <p className="marketing-subtitle marketing-reveal" data-delay="2" style={{ margin: '0 auto' }}>
            RiskLo<sup className="reg-mark">®</sup> combines your account, strategy history, and prop-firm consistency rules
            into one live dashboard — so you know whether a setup is safe to size up,
            consistent with payout rules, and worth the capital you're risking.
          </p>

          <div className="marketing-hero-cta-row marketing-reveal" data-delay="3">
            <button type="button" className="marketing-btn marketing-btn-lg" onClick={go(primaryCtaTarget)}>
              {primaryCtaLabel}
            </button>
            <button type="button" className="marketing-btn-ghost marketing-btn-lg" onClick={go('features')}>
              Explore features
            </button>
          </div>

          <div className="marketing-chip-row marketing-reveal" data-delay="4">
            <span className="marketing-chip"><IconCheck size={14} /> NinjaTrader CSV ready</span>
            <span className="marketing-chip"><IconCheck size={14} /> Prop-firm rule aware</span>
            <span className="marketing-chip"><IconCheck size={14} /> Google verified OAuth</span>
            <span className="marketing-chip"><IconCheck size={14} /> NQ / MNQ contract scaling</span>
          </div>

          <div className="marketing-hero-meta marketing-reveal" data-delay="5">
            <span><IconCheck size={14} /> No credit card to sign in</span>
            <span><IconCheck size={14} /> Cancel anytime</span>
            <span><IconCheck size={14} /> {PRO_TRIAL_DAYS}-day Pro trial</span>
          </div>
        </div>

        {/* Hero preview card with animated mini-dashboard */}
        <div className="marketing-hero-preview marketing-reveal" data-delay="3" aria-hidden="true">
          <div className="marketing-hero-preview-inner">
            <div className="marketing-mock-toolbar">
              <span className="marketing-mock-dot" />
              <span className="marketing-mock-dot" />
              <span className="marketing-mock-dot" />
              <span className="marketing-mock-pill">RiskLo • Strategy Results</span>
            </div>
            <div className="marketing-mock-body">
              <div>
                <div className="marketing-mock-card">
                  <div className="marketing-mock-label">Account blowout risk</div>
                  <div className="marketing-mock-value go">GO</div>
                  <div className="marketing-mock-bar" style={{ '--w': '18%' }}><span /></div>
                  <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'rgba(255,255,255,0.55)' }}>
                    2 contracts • 50K account
                  </div>
                </div>
                <div className="marketing-mock-grid">
                  <div className="marketing-mock-card">
                    <div className="marketing-mock-label">% Rule</div>
                    <div className="marketing-mock-value">SAFE</div>
                  </div>
                  <div className="marketing-mock-card">
                    <div className="marketing-mock-label">Worst day</div>
                    <div className="marketing-mock-value warn">−$842</div>
                  </div>
                </div>
              </div>

              <div className="marketing-mock-card" style={{ display: 'flex', flexDirection: 'column' }}>
                <div className="marketing-mock-label">Weekly equity (sample)</div>
                <svg className="marketing-mock-chart" viewBox="0 0 600 160" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="mockLine" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#818cf8" />
                      <stop offset="100%" stopColor="#c4b5fd" />
                    </linearGradient>
                    <linearGradient id="mockFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="rgba(129, 140, 248, 0.5)" />
                      <stop offset="100%" stopColor="rgba(129, 140, 248, 0)" />
                    </linearGradient>
                  </defs>
                  <path
                    className="fill"
                    d="M0,120 L40,108 L80,90 L120,98 L160,72 L200,80 L240,60 L280,52 L320,68 L360,40 L400,46 L440,28 L480,34 L520,22 L560,16 L600,10 L600,160 L0,160 Z"
                  />
                  <path
                    className="line"
                    d="M0,120 L40,108 L80,90 L120,98 L160,72 L200,80 L240,60 L280,52 L320,68 L360,40 L400,46 L440,28 L480,34 L520,22 L560,16 L600,10"
                  />
                </svg>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem', color: 'rgba(255,255,255,0.55)', fontSize: '0.78rem' }}>
                  <span>Mon</span><span>Tue</span><span>Wed</span><span>Thu</span><span>Fri</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== Trust strip ============== */}
      <section className="marketing-section tight marketing-trust-strip">
        <div className="marketing-container marketing-trust-inner">
          <span><IconShield size={16} /> Google verified OAuth</span>
          <span><IconLock size={16} /> Read-only access</span>
          <span><IconCheck size={16} /> SSL encrypted</span>
          <span><IconCheck size={16} /> No broker credentials stored</span>
        </div>
      </section>

      {/* ============== The problem ============== */}
      <section className="marketing-section">
        <div className="marketing-container marketing-container-narrow marketing-section-heading marketing-reveal">
          <div className="marketing-eyebrow">The problem</div>
          <h2 className="marketing-title">Risk math is too hard to do in your head.</h2>
          <p className="marketing-subtitle">
            Prop-firm consistency rules, scaling, drawdown thresholds, windfall logic,
            historical worst losses — and you're supposed to compute all of it
            <em> before</em> the next setup forms. Most traders just don't, which is how accounts blow.
          </p>
        </div>

        <div className="marketing-container">
          <div className="marketing-stats">
            <div className="marketing-stat marketing-reveal">
              <div className="marketing-stat-value">40</div>
              <div className="marketing-stat-label">accounts analyzed at once</div>
            </div>
            <div className="marketing-stat marketing-reveal" data-delay="1">
              <div className="marketing-stat-value">20–60%</div>
              <div className="marketing-stat-label">consistency rules supported</div>
            </div>
            <div className="marketing-stat marketing-reveal" data-delay="2">
              <div className="marketing-stat-value">NQ / MNQ</div>
              <div className="marketing-stat-label">contract scaling automatic</div>
            </div>
            <div className="marketing-stat marketing-reveal" data-delay="3">
              <div className="marketing-stat-value">&lt; 5s</div>
              <div className="marketing-stat-label">to assess a strategy</div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== Solutions: built for every trader ============== */}
      <section className="marketing-section">
        <div className="marketing-container">
          <div className="marketing-section-heading marketing-reveal">
            <div className="marketing-eyebrow">Built for every trader</div>
            <h2 className="marketing-title">Wherever you trade, RiskLo has your back.</h2>
          </div>
          <div className="mf-solutions">
            {SOLUTIONS.map((sol, i) => (
              <button
                key={sol.id}
                type="button"
                className="mf-solution ml-solution marketing-reveal"
                data-delay={i + 1}
                onClick={() => (sol.page ? goToSection(onNavigate, sol.page) : goToSection(onNavigate, 'features', anchorFor('solution', sol.id)))}
              >
                <span className={`mnav-icon tone-${sol.tone}`}>{sol.icon}</span>
                <h3>{sol.title}</h3>
                <p>{sol.short}</p>
                <span className="ml-more">Learn more →</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ============== Products grid ============== */}
      <section className="marketing-section" id="features">
        <div className="marketing-container">
          <div className="marketing-section-heading marketing-reveal">
            <div className="marketing-eyebrow">One platform</div>
            <h2 className="marketing-title">Everything you need to trade with less risk.</h2>
            <p className="marketing-subtitle" style={{ margin: '1rem auto 0' }}>
              Your accounts, your firm’s rules and your strategies in one place, with an AI that knows all of it.
            </p>
          </div>

          <div className="marketing-feature-grid ml-products">
            {PRODUCTS.map((p, i) => (
              <button
                key={p.id}
                type="button"
                className="marketing-feature-card ml-product marketing-reveal"
                data-delay={(i % 4) + 1}
                onClick={() => goToSection(onNavigate, 'features', anchorFor('product', p.id))}
              >
                <span className={`mnav-icon tone-${p.tone}`}>{p.icon}</span>
                <h3 className="marketing-feature-title">{p.title}</h3>
                <p className="marketing-feature-text">{p.short}</p>
                <span className={`marketing-feature-pill ml-pill-${p.tag.toLowerCase()}`}>{p.tag}</span>
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2.5rem' }}>
            <button type="button" className="marketing-btn-ghost" onClick={go('features')}>
              See every feature in detail →
            </button>
          </div>
        </div>
      </section>

      {/* ============== RiskLo AI ============== */}
      <section className="marketing-section">
        <div className="marketing-container">
          <div className="marketing-split">
            <div className="marketing-split-text marketing-reveal">
              <div className="marketing-eyebrow">RiskLo AI <span className="mp-new">New</span></div>
              <h3 className="marketing-split-title"><span>Ask anything about your trading.</span></h3>
              <p>
                RiskLo AI reads your connected accounts, your trades and the market calendar, and knows every prop firm
                rule we track. Ask in plain English and get plain answers with the numbers behind them.
              </p>
              <ul className="marketing-split-list">
                <li><IconCheck size={16} /> “How did my accounts do this month?”</li>
                <li><IconCheck size={16} /> “What’s on the calendar before Friday’s close?”</li>
                <li><IconCheck size={16} /> “How close am I to my Apex drawdown?”</li>
                <li><IconCheck size={16} /> Facts and math only. It never tells you what to trade.</li>
              </ul>
              <button type="button" className="marketing-btn" style={{ marginTop: '1.25rem' }} onClick={() => goToSection(onNavigate, 'features', anchorFor('product', 'ai'))}>
                Meet RiskLo AI
              </button>
            </div>
            <div className="marketing-split-visual marketing-reveal" data-delay="1">
              <AiVisual />
            </div>
          </div>
        </div>
      </section>

      {/* ============== How it works ============== */}
      <section className="marketing-section">
        <div className="marketing-container">
          <div className="marketing-section-heading marketing-reveal">
            <div className="marketing-eyebrow">How it works</div>
            <h2 className="marketing-title">Three steps to a risk read.</h2>
          </div>

          <div className="marketing-steps">
            <div className="marketing-step marketing-reveal">
              <h4>Pick the strategy</h4>
              <p>
                Choose from your brand&apos;s strategies, paste your own data, or drag in NinjaTrader CSV exports.
                RiskLo automatically aligns instruments, contracts, and account sizing.
              </p>
            </div>
            <div className="marketing-step marketing-reveal" data-delay="1">
              <h4>Enter your account</h4>
              <p>
                Drop in account size, drawdown, contracts, and (if applicable) the consistency rule
                from your prop firm. Defaults are tuned to the rule sets used by major prop firms.
              </p>
            </div>
            <div className="marketing-step marketing-reveal" data-delay="2">
              <h4>Get a GO / NO GO</h4>
              <p>
                See blowout probability, worst-day loss scaled to your contracts, consistency-limit
                exposure, and a clean recommendation — in under five seconds.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============== Detailed showcase: Strategy Results ============== */}
      <section className="marketing-section">
        <div className="marketing-container">
          <div className="marketing-split">
            <div className="marketing-split-text marketing-reveal">
              <div className="marketing-eyebrow"><IconChart size={14} /> Strategy Results</div>
              <h3>Stop spreadsheet-hunting. See every strategy in one dashboard.</h3>
              <p>
                RiskLo transforms raw strategy results into grouped strategy cards,
                weekday breakdowns, weekly totals, and configurable views — past results,
                summary, current results, or every individual strategy.
              </p>
              <ul className="marketing-split-list">
                <li><IconCheck size={16} /> Filter by date range, category, or search</li>
                <li><IconCheck size={16} /> Sort by priority, total, worst day, or consistency</li>
                <li><IconCheck size={16} /> NQ / MNQ contract scaling on the fly</li>
                <li><IconCheck size={16} /> Roll-week and high-impact event indicators</li>
              </ul>
            </div>
            <div className="marketing-split-visual marketing-reveal" data-delay="1">
              <div className="marketing-mock-toolbar" style={{ borderRadius: 12, marginBottom: 12 }}>
                <span className="marketing-mock-dot" />
                <span className="marketing-mock-dot" />
                <span className="marketing-mock-dot" />
                <span className="marketing-mock-pill">Strategy Results · Current</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
                {[1, 2, 3, 4, 5].map((d) => (
                  <div
                    key={d}
                    className="marketing-mock-card"
                    style={{ textAlign: 'center', padding: '0.7rem' }}
                  >
                    <div className="marketing-mock-label" style={{ fontSize: '0.65rem' }}>
                      {['Mon','Tue','Wed','Thu','Fri'][d-1]}
                    </div>
                    <div
                      className="marketing-mock-value"
                      style={{
                        fontSize: '1rem',
                        color: [180, 220, 80, 320, 250][d - 1] >= 0 ? '#34d399' : '#ef4444',
                      }}
                    >
                      ${[180, 220, 80, 320, 250][d - 1]}
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div className="marketing-mock-card">
                  <div className="marketing-mock-label">Weekly total</div>
                  <div className="marketing-mock-value" style={{ color: '#c4b5fd' }}>+$1,050</div>
                </div>
                <div className="marketing-mock-card">
                  <div className="marketing-mock-label">Win days</div>
                  <div className="marketing-mock-value">5 / 5</div>
                </div>
              </div>
            </div>
          </div>

          {/* Portfolio Simulator */}
          <div className="marketing-split reverse">
            <div className="marketing-split-text marketing-reveal">
              <div className="marketing-eyebrow"><IconDashboard size={14} /> Portfolio Simulator</div>
              <h3>Test the whole sleeve — not one strategy at a time.</h3>
              <p>
                Combine strategies into a portfolio and see drawdown, daily P&L, and
                consistency exposure across the entire book. Stop discovering correlation
                the hard way.
              </p>
              <ul className="marketing-split-list">
                <li><IconCheck size={16} /> Strategy sleeves with per-strategy multipliers</li>
                <li><IconCheck size={16} /> Portfolio-level worst day and worst week</li>
                <li><IconCheck size={16} /> Date-range filtering matched to your brand&apos;s views</li>
                <li><IconCheck size={16} /> Beta vs SPY / QQQ / NQ benchmarks</li>
              </ul>
            </div>
            <div className="marketing-split-visual marketing-reveal" data-delay="1">
              <div className="marketing-mock-card" style={{ background: 'rgba(0,0,0,0.2)' }}>
                <div className="marketing-mock-label">Portfolio equity</div>
                <svg className="marketing-mock-chart" viewBox="0 0 600 160" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="mockLine2" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#34d399" />
                      <stop offset="100%" stopColor="#818cf8" />
                    </linearGradient>
                    <linearGradient id="mockFill2" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="rgba(52, 211, 153, 0.4)" />
                      <stop offset="100%" stopColor="rgba(52, 211, 153, 0)" />
                    </linearGradient>
                  </defs>
                  <path
                    fill="url(#mockFill2)"
                    d="M0,140 L60,128 L120,110 L180,118 L240,84 L300,98 L360,68 L420,52 L480,36 L540,24 L600,12 L600,160 L0,160 Z"
                  />
                  <path
                    stroke="url(#mockLine2)"
                    strokeWidth="2.5"
                    fill="none"
                    d="M0,140 L60,128 L120,110 L180,118 L240,84 L300,98 L360,68 L420,52 L480,36 L540,24 L600,12"
                  />
                </svg>
              </div>
              <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                <div className="marketing-mock-card" style={{ padding: '0.65rem' }}>
                  <div className="marketing-mock-label" style={{ fontSize: '0.65rem' }}>Worst day</div>
                  <div className="marketing-mock-value" style={{ fontSize: '0.95rem', color: '#ef4444' }}>−$1,180</div>
                </div>
                <div className="marketing-mock-card" style={{ padding: '0.65rem' }}>
                  <div className="marketing-mock-label" style={{ fontSize: '0.65rem' }}>Win rate</div>
                  <div className="marketing-mock-value" style={{ fontSize: '0.95rem' }}>72%</div>
                </div>
                <div className="marketing-mock-card" style={{ padding: '0.65rem' }}>
                  <div className="marketing-mock-label" style={{ fontSize: '0.65rem' }}>Sleeves</div>
                  <div className="marketing-mock-value" style={{ fontSize: '0.95rem' }}>5</div>
                </div>
              </div>
            </div>
          </div>

          {/* Bulk + CSV */}
          <div className="marketing-split">
            <div className="marketing-split-text marketing-reveal">
              <div className="marketing-eyebrow"><IconList size={14} /> Bulk Risk + CSV</div>
              <h3>Score 40 accounts in one pass.</h3>
              <p>
                Drag NinjaTrader Accounts and Strategies exports in. RiskLo detects column
                schemas automatically, matches strategies to your brand&apos;s names, and seeds the
                bulk grid so you can run blowout and % Rule together.
              </p>
              <ul className="marketing-split-list">
                <li><IconCheck size={16} /> Auto-detect NinjaTrader CSV type</li>
                <li><IconCheck size={16} /> NQ ↔ MNQ instrument scaling</li>
                <li><IconCheck size={16} /> Start-of-day profit derived per account</li>
                <li><IconCheck size={16} /> Optional email summary after each run</li>
              </ul>
            </div>
            <div className="marketing-split-visual marketing-reveal" data-delay="1">
              <div className="marketing-mock-card" style={{ padding: 0, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.6)' }}>
                    <tr>
                      {['Account','Strategy','Size','Risk','State'].map((h) => (
                        <th key={h} style={{ textAlign: 'left', padding: '0.6rem 0.75rem', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['PAAPEX...001','Grass Fed','$50K','LOW','GO'],
                      ['PAAPEX...002','Tactical','$100K','MED','GO'],
                      ['PAAPEX...003','Momentum','$50K','HIGH','NO GO'],
                      ['PAAPEX...004','Sniper','$25K','LOW','GO'],
                      ['PAAPEX...005','Vault','$150K','MED','GO'],
                    ].map((row, i) => (
                      <tr key={i} style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                        {row.map((c, j) => (
                          <td
                            key={j}
                            style={{
                              padding: '0.55rem 0.75rem',
                              color: j === 4
                                ? c === 'GO' ? '#34d399' : '#ef4444'
                                : 'rgba(255,255,255,0.78)',
                              fontWeight: j === 4 ? 700 : 500,
                            }}
                          >{c}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== Pricing preview ============== */}
      <section className="marketing-section" id="pricing-preview">
        <div className="marketing-container">
          <div className="marketing-section-heading marketing-reveal">
            <div className="marketing-eyebrow"><IconStar size={14} /> Simple pricing</div>
            <h2 className="marketing-title">Free to start. Pro when you scale.</h2>
            <p className="marketing-subtitle" style={{ margin: '1rem auto 0' }}>
              Start without a credit card. Upgrade when you want your accounts, the risk tools and RiskLo AI
              working together.
            </p>
          </div>

          <div className="marketing-pricing-grid two-cols">
            <div className="marketing-plan marketing-reveal">
              <span className="marketing-plan-name">Basic</span>
              <div className="marketing-plan-price">$0<small>/forever</small></div>
              <p className="marketing-plan-tagline">For traders exploring strategy results.</p>
              <ul className="marketing-plan-list">
                <li><IconCheck size={16} /> Your company’s strategy results</li>
                <li><IconCheck size={16} /> NQ / MNQ contract scaling</li>
                <li><IconCheck size={16} /> Position Sizer</li>
                <li><IconCheck size={16} /> Previews of the risk tools</li>
                <li><IconCheck size={16} /> Guides and tutorials</li>
              </ul>
              <button type="button" className="marketing-btn-ghost marketing-plan-cta" onClick={go('login')}>
                Get started
              </button>
            </div>

            <div className="marketing-plan featured marketing-reveal" data-delay="1">
              <span className="marketing-plan-badge">Most popular</span>
              <span className="marketing-plan-name">Pro</span>
              <div className="marketing-plan-price">{proPriceFor().label}<small>/month</small></div>
              <p className="marketing-plan-tagline">
                {proPriceFor().partner
                  ? `Member price · $${PRO_PRICE}/month on your own`
                  : `$${PRO_PARTNER_PRICE}/month through a partner like Vector`}
              </p>
              <ul className="marketing-plan-list">
                <li><IconCheck size={16} /> Everything in Basic</li>
                <li><IconCheck size={16} /> RiskLo AI · {AI_MONTHLY_QUESTIONS.pro} questions a month</li>
                <li><IconCheck size={16} /> My Portfolio · up to 5 broker accounts</li>
                <li><IconCheck size={16} /> Blowout Risk, % Rule and Hedging Risk</li>
                <li><IconCheck size={16} /> Portfolio Simulator and Bulk Risk</li>
                <li><IconCheck size={16} /> Portfolio risk tools and custom dashboards</li>
                <li><IconCheck size={16} /> Email risk summaries</li>
              </ul>
              <button type="button" className="marketing-btn marketing-plan-cta" onClick={go('pricing')}>
                Start {PRO_TRIAL_DAYS}-day free trial
              </button>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.75rem' }}>
            <button type="button" className="marketing-btn-ghost" onClick={go('pricing')}>
              Compare every feature →
            </button>
          </div>
        </div>
      </section>

      {/* ============== FAQ preview ============== */}
      <section className="marketing-section">
        <div className="marketing-container">
          <div className="marketing-section-heading marketing-reveal">
            <div className="marketing-eyebrow">FAQ</div>
            <h2 className="marketing-title">Answers, before you sign up.</h2>
          </div>

          <div className="marketing-faq">
            {HOMEPAGE_FAQ.map((item, i) => (
              <div key={item.q} className="marketing-reveal" data-delay={i + 1}>
                <FaqItem q={item.q} a={item.a} defaultOpen={i === 0} />
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2rem' }}>
            <button type="button" className="marketing-btn-ghost" onClick={go('help')}>
              Read full FAQ →
            </button>
          </div>
        </div>
      </section>

      {/* ============== Final CTA ============== */}
      <section className="marketing-section">
        <div className="marketing-container">
          <div className="marketing-cta marketing-reveal">
            <div className="marketing-cta-inner">
              <div className="marketing-eyebrow"><IconZap size={14} /> Ready when you are</div>
              <h2>Know your risk before the next setup forms.</h2>
              <p>
                RiskLo is built for the trader who wants to size up without blowing
                an account. Run a strategy through it in under a minute — you'll
                see what we mean.
              </p>
              <div className="marketing-hero-cta-row" style={{ marginTop: 0 }}>
                <button type="button" className="marketing-btn marketing-btn-lg" onClick={go(primaryCtaTarget)}>
                  {primaryCtaLabel}
                </button>
                <button type="button" className="marketing-btn-ghost marketing-btn-lg" onClick={go('pricing')}>
                  See pricing
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default LandingPage;
